// Dashboards hub — the registry of every dashboard in X-One and the
// rules for who sees which.
//
// Pure module (no React, no Vite globals) so the gating, search and
// pin rules are tsx-testable. The new cross-app dashboards contribute
// themselves through `lib/dashboardEntries/*.ts`; dashboardRegistry.ts
// collects those with import.meta.glob and hands them to
// `normalizeContributedEntry` here.
//
// Access mirrors the source pages exactly. The hub never shows a card
// the person couldn't open: no sales tiles, no locked rows.
//   - app entitlement: the launcher / sidebar rule — `app.<key>` held
//     AND the app-level flag (key `app.<key>`) not switched off;
//   - adminOnly: the source page shows members an admin-only notice
//     instead of its content (or its api is owner/admin only);
//   - flag / entitlement: the route's FeatureFlagRoute and any extra
//     entitlement the page checks (AI insights).

import type { AppKey } from './apps';

/** Which section of the hub a card belongs to. */
export type DashboardKind = 'cross-app' | 'app' | 'kpi';

export interface DashboardEntry {
  /** Stable key, also the pin key. Lowercase, `[a-z0-9_:-]`. */
  key: string;
  title: string;
  /** One customer-facing sentence. */
  description: string;
  route: string;
  /** Lucide icon name in kebab-case; the page maps it to a component. */
  icon: string;
  kind: DashboardKind;
  /** The app that owns the page (kind 'app'); null otherwise. */
  app: AppKey | null;
  /** Cross-app dashboards: shown when the org holds at least one of
   *  these apps. Empty = organization-wide, always shown. */
  apps: readonly AppKey[];
  adminOnly: boolean;
  /** Feature flag the route is wrapped in; hidden when switched off. */
  flag?: string;
  /** Extra entitlement the source page requires. */
  entitlement?: string;
}

export const APP_LABELS: Record<AppKey, string> = {
  aware: 'Aware',
  xes: 'Email Security',
  posture: 'Posture',
  surface: 'Surface',
  exposure: 'Exposure',
  agents: 'Agents',
  orchestrate: 'Orchestrate',
  insider: 'Insider',
  resilience: 'Resilience',
  'ai-gateway': 'AI Gateway',
  'ai-security': 'AI Security',
  umbra: 'Umbra',
};

/** Display order of the "By app" groups and the app filter chips. */
export const APP_ORDER: readonly AppKey[] = [
  'aware',
  'xes',
  'posture',
  'surface',
  'exposure',
  'insider',
  'resilience',
  'agents',
  'orchestrate',
  'ai-gateway',
  'ai-security',
  'umbra',
];

function crossApp(e: Omit<DashboardEntry, 'kind' | 'app' | 'apps'> & { apps?: readonly AppKey[] }): DashboardEntry {
  return { ...e, kind: 'cross-app', app: null, apps: e.apps ?? [] };
}

function appDashboard(app: AppKey, e: Omit<DashboardEntry, 'kind' | 'app' | 'apps'>): DashboardEntry {
  return { ...e, kind: 'app', app, apps: [] };
}

/** Existing views, verified against App.tsx routes and each page's own
 *  gate. Agents, Orchestrate and Insider open on lists, not overviews,
 *  so they have no entry. */
export const BUILT_IN_DASHBOARDS: readonly DashboardEntry[] = [
  crossApp({
    key: 'security-overview',
    title: 'Security overview',
    description: 'Your organization’s security across every app at a glance, with what needs attention first.',
    route: '/launcher',
    icon: 'home',
    adminOnly: false,
  }),
  crossApp({
    key: 'member-risk',
    title: 'Member risk',
    description: 'Which people are most at risk, based on how they handle phishing, training and email.',
    route: '/risk',
    icon: 'gauge',
    adminOnly: true,
  }),
  crossApp({
    key: 'ai-insights',
    title: 'AI insights',
    description: 'A weekly summary of what changed across your security programs and what to do next.',
    route: '/insights',
    icon: 'sparkles',
    adminOnly: true,
    flag: 'ai.insights',
    entitlement: 'aiInsights',
  }),
  crossApp({
    key: 'reports',
    title: 'Reports',
    description: 'Build a report on your security program to share with your board or auditors.',
    route: '/reports',
    icon: 'file-text',
    adminOnly: true,
  }),
  crossApp({
    key: 'kpis',
    title: 'KPIs',
    description: 'Track the measures that matter to you against targets you set.',
    route: '/kpi',
    icon: 'target',
    adminOnly: true,
  }),
  appDashboard('aware', {
    key: 'aware-overview',
    title: 'Awareness overview',
    description: 'Training progress and phishing results for your people, with what to do next.',
    route: '/aware',
    icon: 'graduation-cap',
    adminOnly: false,
  }),
  appDashboard('aware', {
    key: 'aware-phishing-analytics',
    title: 'Phishing analytics',
    description: 'How your people respond to phishing simulations over time: who clicks, who reports, and how fast.',
    route: '/aware/phishing/analytics',
    icon: 'activity',
    adminOnly: true,
  }),
  appDashboard('xes', {
    key: 'xes-overview',
    title: 'Email security overview',
    description: 'How suspicious email is trending in your organization and how quickly it’s handled.',
    route: '/xes/dashboard',
    icon: 'mail',
    adminOnly: true,
  }),
  appDashboard('posture', {
    key: 'posture-overview',
    title: 'Posture overview',
    description: 'The security health of your devices, cloud and SaaS in one view, with the issues to fix first.',
    route: '/posture/overview',
    icon: 'laptop',
    adminOnly: true,
  }),
  appDashboard('posture', {
    key: 'posture-scan-overview',
    title: 'Scan overview',
    description: 'Results of your latest posture scans: what was checked, what failed and where.',
    route: '/posture/overview/scan',
    icon: 'scan-search',
    adminOnly: true,
  }),
  appDashboard('posture', {
    key: 'posture-pipeline',
    title: 'Detection to resolution',
    description: 'How issues move from first detection to resolution, and where they get stuck.',
    route: '/posture/overview/pipeline',
    icon: 'git-branch',
    adminOnly: true,
  }),
  appDashboard('posture', {
    key: 'posture-cloud-compliance',
    title: 'Cloud compliance',
    description: 'How your cloud measures up against frameworks such as CIS, SOC 2 and ISO 27001.',
    route: '/posture/iaas/compliance',
    icon: 'shield-check',
    adminOnly: true,
  }),
  appDashboard('surface', {
    key: 'surface-overview',
    title: 'External risk',
    description: 'What attackers can see of your organization from the internet, and which exposures matter most.',
    route: '/surface/findings',
    icon: 'globe',
    adminOnly: true,
  }),
  appDashboard('resilience', {
    key: 'resilience-overview',
    title: 'Resilience overview',
    description: 'What is under protection, whether its recovery points have been checked, and what needs attention.',
    route: '/resilience',
    icon: 'archive',
    adminOnly: false,
    flag: 'resilience.tenantSurface',
  }),
];

const KEY_RE = /^[a-z0-9_:-]{1,64}$/;
const APP_KEYS = new Set<string>(APP_ORDER);

/** Validate one `dashboardEntries/*.ts` module export. Returns null for
 *  anything that doesn't match the contract, so a malformed file can't
 *  break the page. */
export function normalizeContributedEntry(raw: unknown): DashboardEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const str = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
  if (!str(r.key) || !KEY_RE.test(r.key) || r.key.startsWith('kpi:')) return null;
  if (!str(r.title) || !str(r.description) || !str(r.route) || !r.route.startsWith('/')) return null;
  if (!Array.isArray(r.apps) || !r.apps.every((a) => typeof a === 'string' && APP_KEYS.has(a))) return null;
  // MP900 — an entry may name the app that owns its page (`app`); it then
  // lists under that app and needs it, on top of any `apps` it reads from.
  if (r.app !== undefined && !(typeof r.app === 'string' && APP_KEYS.has(r.app))) return null;
  const app = (r.app as AppKey | undefined) ?? null;
  return {
    key: r.key,
    title: r.title,
    description: r.description,
    route: r.route,
    icon: str(r.icon) ? r.icon : 'layout-dashboard',
    kind: app ? 'app' : 'cross-app',
    app,
    apps: r.apps as AppKey[],
    adminOnly: r.adminOnly === true,
    // Optional gates a dashboard's own route applies (a feature flag or
    // an extra entitlement), so the hub never shows a card that bounces.
    ...(str(r.flag) ? { flag: r.flag } : {}),
    ...(str(r.entitlement) ? { entitlement: r.entitlement } : {}),
  };
}

/** Built-ins plus contributed entries. A contributed entry whose key is
 *  already taken is dropped (built-ins win, then first file wins). */
export function mergeDashboards(
  builtIns: readonly DashboardEntry[],
  contributed: readonly unknown[],
): DashboardEntry[] {
  const out = [...builtIns];
  const seen = new Set(out.map((e) => e.key));
  for (const raw of contributed) {
    const e = normalizeContributedEntry(raw);
    if (!e || seen.has(e.key)) continue;
    seen.add(e.key);
    out.push(e);
  }
  return out;
}

export interface KpiDashboardSummary {
  id: string;
  name: string;
  description?: string;
}

export function kpiDashboardKey(id: string): string {
  return `kpi:${id}`;
}

/** Saved KPI dashboards as hub entries. The KPI pages and their api are
 *  owner/admin only. Ids that can't form a valid pin key are skipped. */
export function kpiDashboardEntries(list: readonly KpiDashboardSummary[]): DashboardEntry[] {
  return list
    .filter((d) => KEY_RE.test(kpiDashboardKey(d.id)))
    .map((d) => ({
      key: kpiDashboardKey(d.id),
      title: d.name,
      description: d.description?.trim() || 'A saved set of KPIs with their current status against target.',
      route: `/kpi/dashboards/${encodeURIComponent(d.id)}`,
      icon: 'layout-grid',
      kind: 'kpi' as const,
      app: null,
      apps: [],
      adminOnly: true,
    }));
}

export interface DashboardAccess {
  isAdmin: boolean;
  /** The org's effective entitlement map (null while loading). */
  entitlements: Record<string, unknown> | null;
  /** Keys of flags that resolve to disabled for this org. A flag that
   *  isn't in the resolved set passes open (MP316i). */
  disabledFlags: ReadonlySet<string>;
}

function isEnabled(value: unknown): boolean {
  if (typeof value === 'number') return value !== 0;
  return !!value;
}

/** The launcher's rule: entitled to `app.<key>` and its app-level flag
 *  isn't switched off. */
export function hasApp(app: AppKey, access: DashboardAccess): boolean {
  const key = `app.${app}`;
  return isEnabled(access.entitlements?.[key]) && !access.disabledFlags.has(key);
}

export function canSeeDashboard(e: DashboardEntry, access: DashboardAccess): boolean {
  if (e.adminOnly && !access.isAdmin) return false;
  if (e.flag && access.disabledFlags.has(e.flag)) return false;
  if (e.entitlement && !isEnabled(access.entitlements?.[e.entitlement])) return false;
  if (e.app && !hasApp(e.app, access)) return false;
  if (e.apps.length > 0 && !e.apps.some((a) => hasApp(a, access))) return false;
  return true;
}

/** 'all', 'cross-app' (organization-wide views and saved KPI
 *  dashboards) or one app. */
export type DashboardFilter = 'all' | 'cross-app' | AppKey;

export function matchesFilter(e: DashboardEntry, filter: DashboardFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'cross-app') return e.kind !== 'app';
  return e.app === filter || e.apps.includes(filter);
}

export function matchesSearch(e: DashboardEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const appLabel = e.app ? APP_LABELS[e.app] : '';
  return [e.title, e.description, appLabel].some((s) => s.toLowerCase().includes(q));
}

/** Pinned entries in pin order. Keys that don't resolve to a visible
 *  entry (retired dashboard, deleted KPI dashboard, another org's
 *  dashboard, no longer entitled) are ignored. */
export function resolvePins(keys: readonly string[], visible: readonly DashboardEntry[]): DashboardEntry[] {
  const byKey = new Map(visible.map((e) => [e.key, e]));
  return keys.flatMap((k) => byKey.get(k) ?? []);
}

export const MAX_PINS = 12;

// The sidebar's Dashboards rows are already filtered to what this person
// can open, so links built from them can never be dead.
export const PINNED_GROUP = 'Pinned';
const EXECUTIVE_ROUTE = '/dashboards/executive';

interface DashboardMenuRow {
  to: string;
  label: string;
  group?: string;
}

/** Links for Home: pinned dashboards, in pin order. The leadership focus
 *  leads with the Executive dashboard, when this person can open it. */
export function homeDashboardLinks<T extends DashboardMenuRow>(menu: readonly T[] | null, leadership: boolean): T[] {
  const rows = menu ?? [];
  const pinned = rows.filter((r) => r.group === PINNED_GROUP);
  const executive = leadership ? rows.find((r) => r.to === EXECUTIVE_ROUTE) : undefined;
  return executive ? [executive, ...pinned.filter((r) => r !== executive)] : pinned;
}

/** The hub and the other cross-app dashboards, without the current one. */
export function relatedDashboardLinks<T extends DashboardMenuRow>(menu: readonly T[] | null, selfRoute: string): T[] {
  return (menu ?? []).filter((r) => r.to !== selfRoute && (r.to === '/dashboards' || r.to.startsWith('/dashboards/')));
}

/** Toggle a pin. Pinning adds to the end; returns null when the list is
 *  full. Pins that don't resolve here are kept (they may belong to
 *  another org the person is in) unless the list is full, in which case
 *  they make room first. */
export function togglePin(
  keys: readonly string[],
  key: string,
  liveKeys: ReadonlySet<string>,
): string[] | null {
  if (keys.includes(key)) return keys.filter((k) => k !== key);
  const kept = keys.length >= MAX_PINS ? keys.filter((k) => liveKeys.has(k)) : [...keys];
  if (kept.length >= MAX_PINS) return null;
  return [...kept, key];
}
